import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface UIState {
  // Sidebar state
  sidebarCollapsed: boolean
  agentSidebarWidth: number
  hasAutoCollapsedAdminSidebar: boolean
  hasHydrated: boolean

  // Actions
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setAgentSidebarWidth: (width: number) => void
  ensureAdminSidebarCollapsed: () => void
  setHasHydrated: (hydrated: boolean) => void
}

export const useUIStore = create<UIState>()(
  persist(
    set => ({
      // Initial state
      sidebarCollapsed: false,
      agentSidebarWidth: 320,
      hasAutoCollapsedAdminSidebar: false,
      hasHydrated: false,

      // Actions
      toggleSidebar: () => {
        set(state => ({ sidebarCollapsed: !state.sidebarCollapsed }))
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed })
      },

      setAgentSidebarWidth: (width: number) => {
        set({ agentSidebarWidth: Math.max(120, width) })
      },
      ensureAdminSidebarCollapsed: () => {
        set(state => {
          if (state.hasAutoCollapsedAdminSidebar) {
            return {}
          }

          return {
            sidebarCollapsed: true,
            hasAutoCollapsedAdminSidebar: true,
          }
        })
      },
      setHasHydrated: hydrated => {
        set({ hasHydrated: hydrated })
      },
    }),
    {
      name: 'ui-store',
      partialize: state => ({
        sidebarCollapsed: state.sidebarCollapsed,
        agentSidebarWidth: state.agentSidebarWidth,
        hasAutoCollapsedAdminSidebar: state.hasAutoCollapsedAdminSidebar,
      }),
      onRehydrateStorage: () => state => {
        state?.setHasHydrated(true)
      },
    }
  )
)

// Selectors for easier access
export const useSidebarState = () => {
  const store = useUIStore()
  return {
    sidebarCollapsed: store.sidebarCollapsed,
    toggleSidebar: store.toggleSidebar,
    setSidebarCollapsed: store.setSidebarCollapsed,
    agentSidebarWidth: store.agentSidebarWidth,
    setAgentSidebarWidth: store.setAgentSidebarWidth,
    hasAutoCollapsedAdminSidebar: store.hasAutoCollapsedAdminSidebar,
    ensureAdminSidebarCollapsed: store.ensureAdminSidebarCollapsed,
    hasHydrated: store.hasHydrated,
  }
}
